import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../services/common.service';
import { MatOption } from '@angular/material/core';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styleUrls: ['../styles.css', './filter-dropdown.component.css'],
})
export class FilterDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('select') input: ElementRef | undefined;

  @Input() loading: boolean = false;
  @Input() label: string = '';
  @Input() options: { key: any; value: string; disabled?: boolean }[] = [];
  @Input() enableClearSelection: boolean = false;
  @Input() parentFormGroup: FormGroup = new FormGroup({});
  @Input() controlName: string = '';
  @Input() error: string = '';
  @Input() placeholder: string = '';
  @Input() filterControlName: string = '';
  @Input() searchPlaceholder: string = '';
  @Input() enableSearch: boolean = false;
  @Input() focus: boolean = false;
  @Input() enableMultiple: boolean = false;
  @Input() optionsPanelClassName: string = '';
  @Input() type: string = '';
  @Input() disableAllOptions = false;
  @Input() defaultValue: any;
  @Input() hasNext: boolean = false;
  @Input() enableAllSelection: boolean = false;
  @Output() checkForErrors: EventEmitter<any> = new EventEmitter();
  @Output() onNextPage = new EventEmitter();
  @ViewChild('allOption') private allOption: MatOption;

  throttle = 300;
  scrollDistance = 0.01;
  scrollUpDistance = 0.01;
  isAllSelected: boolean = false;
  fieldSubscription: Subscription | undefined;

  isRequired: boolean = false;
  isDisabled: boolean = false;
  optionsDisabled: boolean = false;
  isNextLoader: boolean = false;

  constructor(private commonService: CommonService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['focus']?.currentValue) {
      const clientArea = $('#client_area');
      const y =
        clientArea.scrollTop() +
        this.input?.nativeElement.getBoundingClientRect().top -
        this.commonService.headerRemOffsetCalculator();

      clientArea.scrollTop(y);
    }
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event: any) {
    if (!this.error) {
      this.parentFormGroup?.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';
    this.fieldSubscription = control?.valueChanges.subscribe((value) => {
      this.isChecked();

      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup?.controls[this.controlName].touched) {
        this.parentFormGroup?.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup.controls[this.controlName].errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  clearSelection($event: Event) {
    $event.stopPropagation();
    this.parentFormGroup.controls[this.controlName].setValue(
      this.defaultValue || '',
    );
    this.parentFormGroup.markAsDirty();
  }

  isChecked(): boolean {
    return (
      this.parentFormGroup.controls[this.controlName].value &&
      this.options.length &&
      this.parentFormGroup.controls[this.controlName].value.length ===
        this.options.length
    );
  }

  isIndeterminate(): boolean {
    const control = this.parentFormGroup.controls[this.controlName];

    return !!(
      Array.isArray(control.value) &&
      control.value?.length &&
      control.value?.length < this.options?.length
    );
  }

  getNext() {
    if (this.loading || !this.hasNext) {
      return;
    }
    this.isNextLoader = true;
    this.onNextPage.emit();
  }

  toggleSelection(change: MatCheckboxChange): void {
    if (change.checked) {
      this.parentFormGroup.controls[this.controlName].setValue(
        this.options.map((o) => o.key),
      );
    } else {
      this.parentFormGroup.controls[this.controlName].setValue([]);
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }

  getIntensityIcon(value: string): string | null {
    // Return SVG icon name for intensity values
    if (value === '100') {
      return '100 (1)-20250401-134935';
    } else if (value === '200') {
      return '200 (1)-20250401-135016';
    } else if (value === '300') {
      return '300-20250401-135033';
    } else if (value === '400') {
      return '400-20250401-135052';
    }
    return null;
  }

  getSelectedValue(): string {
    const selectedKey = this.parentFormGroup.getRawValue()[this.controlName];
    const option = this.options.find((o) => o.key === selectedKey);
    return option ? option.value : '';
  }

  getSelectedIntensityIcon(): string | null {
    const value = this.getSelectedValue();
    return this.getIntensityIcon(value);
  }
}
